import React from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import { useQuery } from 'react-apollo'
import classNames from 'classnames'
import {
  find,
  lensPath,
  lensProp,
  pathOr,
  pipe,
  propEq,
  reduce,
  sort
} from 'ramda'

import { CardWrapper, CardContent } from '../../../shared/settings/card'
import { LoadingModal } from '../../../loading/modal'
import { DownloadsTable, getTableColumns } from './table-components'
import { viewOr } from '../../../../lib/fp'

import DOWNLOADS_QUERY from '../../../../../graphql/queries/downloads.gql'
import DEVICE_FRAGMENT from '../../../../../graphql/fragments/device.gql'

import styles from './view-downloads-table.css'

const byMostRecent = (a, b) => (
  new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime()
)

const sortDownloads = sort(byMostRecent)
const titleLens = lensPath(['contentItem', 'title'])
const downloadedAtLens = lensProp('downloadedAt')

const getDescription = ({ contentItem: { episodeNumber, seasonNumber, __typename } = {} }) => {
  return __typename === 'Title' ? 'Movie' : `TV / S${seasonNumber} / E${episodeNumber}`
}

const extractDownloadsTableDataObject = (download) => {
  return {
    title: viewOr('', titleLens, download),
    description: getDescription(download),
    downloadedAt: viewOr('', downloadedAtLens, download)
  }
}

const transformDownload = (downloads, download) => {
  return downloads.concat(extractDownloadsTableDataObject(download))
}

const getTableData = pipe(
  pathOr([], ['downloads', 'items']),
  reduce(transformDownload, []),
  sortDownloads
)

const handleTableHeadProps = () => ({
  className: classNames(styles.downloadsTableHead, styles.header)
})

const handleTableRowGroupProps = () => ({
  className: classNames(styles.downloadsTableRowGroup, styles.row)
})

const handleTableHeadThProps = (state, rowInfo, column) => {
  const isContentTitle = column.id === 'title'
  const isContentDownloadedAt = column.id === 'downloadedAt'

  return {
    className: classNames(
      styles.downloadsTableHeadTh,
      { [styles.thFirst]: isContentTitle },
      { [styles.thLast]: isContentDownloadedAt }
    )
  }
}

const handleTableDataProps = (state, rowInfo, column) => {
  const isContentTitle = column.id === 'title'
  const isContentDownloadedAt = column.id === 'downloadedAt'

  return {
    className: classNames(
      styles.downloadsTableData,
      { [styles.tdFirst]: isContentTitle },
      { [styles.tdLast]: isContentDownloadedAt }
    )
  }
}

const handleTableProps = (state, rowInfo, column, instance) => ({
  style: instance.props.data.length === 0 ? { display: 'none' } : {}
})

const ViewDownloadsTable = ({
  devices,
  selectedDeviceUuid,
  selectedDevice,
  ...rest
}) => {
  const {
    data: downloadsData,
    loading: downloadsLoading,
    error: downloadsError
  } = useQuery(DOWNLOADS_QUERY, {
    variables: {
      input: {
        deviceId: selectedDeviceUuid
      }
    }
  })

  if (downloadsLoading || downloadsError) {
    return <LoadingModal />
  }

  const device = selectedDevice || find(propEq('uuid', selectedDeviceUuid))(devices)
  const tableColumns = getTableColumns()
  const deviceDownloads = getTableData(downloadsData)

  if (!device) {
    return null
  }

  return (
    <CardWrapper className={styles.viewDownloadsCardWrapper}>
      <CardContent className={styles.cardContent}>
        <h4 className={styles.selectedDeviceName}>{device.name}</h4>
        <DownloadsTable
          {...rest}
          deviceDownloads={deviceDownloads}
          tableColumns={tableColumns}
          handleTableHeadThProps={handleTableHeadThProps}
          handleTableHeadProps={handleTableHeadProps}
          handleTableRowGroupProps={handleTableRowGroupProps}
          handleTableProps={handleTableProps}
          handleTableDataProps={handleTableDataProps}
        />
      </CardContent>
    </CardWrapper>
  )
}

ViewDownloadsTable.propTypes = {
  devices: PropTypes.arrayOf(propType(DEVICE_FRAGMENT)).isRequired,
  selectedDeviceUuid: PropTypes.string.isRequired,
  selectedDevice: propType(DEVICE_FRAGMENT)
}

ViewDownloadsTable.defaultProps = {
  selectedDevice: null
}

export default ViewDownloadsTable
